import type { RpcCaller } from './micro-rpc-client/RPCClient'

// RPCClient throws an Error with this exact message on a 401 response
// (see ./micro-rpc-client/RPCClient).
const UNAUTHORIZED_ERROR_MESSAGE = 'Unauthorized request'

const isUnauthorizedError = (error: unknown): boolean =>
  error instanceof Error && error.message === UNAUTHORIZED_ERROR_MESSAGE

// Refresh outcome. `impersonation` means the session is impersonated and has no
// refresh token, so the actor stops impersonating instead of logging out.
// `no_session` means the browser sent no refresh token and `failed` means the
// refresh token was rejected; both re-authorize through the authorize route.
// `unavailable` means the refresh could not be carried out, which says nothing
// about the session, so nobody is redirected. `boolean` is accepted for legacy
// callers that predate the outcome (true = refreshed).
export type RefreshResult =
  | boolean
  | 'refreshed'
  | 'impersonation'
  | 'no_session'
  | 'failed'
  | 'unavailable'

export interface AuthedRpcCallerDeps {
  /** The underlying caller whose 401s should trigger a refresh-and-retry. */
  rpc: RpcCaller
  /**
   * Whether OIDC silent refresh is active; false keeps the legacy passthrough.
   * Omit for apps that no longer gate on a flag and always refresh.
   */
  isOidcAuthEnabled?: () => boolean
  /** Refreshes the access token. */
  refreshAccessToken: () => Promise<RefreshResult>
  /**
   * Redirects to the OIDC logout endpoint when the replayed call is still
   * rejected.
   */
  redirectToOidcLogout: () => void
  /** Runs when the refresh reports an impersonated session. */
  redirectToImpersonateStop: () => void
  /**
   * Redirects to the authorize route when the refresh token is missing or
   * rejected.
   */
  redirectToAuthorize: () => void
}

/**
 * Wraps an {@link RpcCaller} so that, when OIDC auth is on, a 401 silently
 * refreshes the access token (shared single-flight + cross-tab-locked helper)
 * and replays the call once. If the refresh reports an impersonated session,
 * stops impersonation instead. If the refresh fails, re-authorizes. If the
 * replay is still unauthorized, redirects to OIDC logout — a single retry only,
 * never a loop.
 * A refresh that could not be carried out at all redirects nowhere and lets the
 * 401 propagate, since the session is not known to be over.
 * With OIDC off, or for any non-auth error, the error propagates unchanged.
 *
 * This mirrors the explicit `callRpc` wrapper for the publish app, extended to
 * any caller so the `async-data-fetch` middleware's RPC traffic gets the same
 * refresh-and-retry instead of failing hard on a 401.
 */
export const createAuthedRpcCaller = ({
  rpc,
  isOidcAuthEnabled = () => true,
  refreshAccessToken,
  redirectToOidcLogout,
  redirectToImpersonateStop,
  redirectToAuthorize,
}: AuthedRpcCallerDeps): RpcCaller => ({
  async call(name, args, headers): Promise<unknown> {
    try {
      return await rpc.call(name, args, headers)
    } catch (error) {
      if (!isOidcAuthEnabled() || !isUnauthorizedError(error)) {
        throw error
      }

      const result = await refreshAccessToken()

      if (result === 'unavailable') {
        throw error
      }

      if (result === 'impersonation') {
        redirectToImpersonateStop()

        throw error
      }

      if (result !== true && result !== 'refreshed') {
        redirectToAuthorize()

        throw error
      }

      // Single retry only — never loop. A second auth rejection means the fresh
      // token is still not accepted, so log out.
      try {
        return await rpc.call(name, args, headers)
      } catch (retryError) {
        if (isUnauthorizedError(retryError)) {
          redirectToOidcLogout()
        }

        throw retryError
      }
    }
  },
})
